import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  IconButton,
  Chip,
  Skeleton,
  InputAdornment,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Divider,
  Badge,
  LinearProgress,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PaymentsIcon from "@mui/icons-material/Payments";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import Swal from "sweetalert2";
import { api, API_BASE } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import JobFilterDrawer, { emptyFilters } from "./JobFilterDrawer";

const statusColor = (s) => {
  const v = (s || "").toLowerCase();
  if (v.includes("complete")) return "success";
  if (v.includes("progress") || v.includes("assign")) return "info";
  if (v.includes("pending")) return "warning";
  if (v.includes("reject")) return "error";
  return "default";
};

const toQuery = (page, pageSize, f) => {
  const q = new URLSearchParams({
    page,
    pageSize,
    search: f.search || "",
    status: (f.status || []).join(","),
    source: (f.source || []).join(","),
    stage: (f.stage || []).join(","),
    client_id: f.client_id || "",
    manager_id: f.manager_id || "",
    assigned_to: f.assigned_to || "",
    date_from: f.date_from || "",
    date_to: f.date_to || "",
    price_min: f.price_min || "",
    price_max: f.price_max || "",
    overdue: f.overdue || "",
    sort_by: f.sort_by || "id",
    sort_order: f.sort_order || "desc",
  });
  return q.toString();
};

const countActive = (f) => {
  let c = 0;
  if (f.search) c++;
  if (f.status?.length) c++;
  if (f.source?.length) c++;
  if (f.stage?.length) c++;
  if (f.client_id) c++;
  if (f.manager_id) c++;
  if (f.assigned_to) c++;
  if (f.date_from) c++;
  if (f.date_to) c++;
  if (f.price_min) c++;
  if (f.price_max) c++;
  if (f.overdue) c++;
  return c;
};

const activeChips = (f, onRemove) => {
  const chips = [];
  const add = (key, label, value) => chips.push({ key, label: `${label}: ${value}` });

  if (f.search) add("search", "Search", f.search);
  if (f.status?.length) add("status", "Status", f.status.join(", "));
  if (f.source?.length) add("source", "Source", f.source.join(", "));
  if (f.stage?.length) add("stage", "Stage", f.stage.join(", "));
  if (f.client_id) add("client_id", "Client", `#${f.client_id}`);
  if (f.manager_id) add("manager_id", "Manager", `#${f.manager_id}`);
  if (f.assigned_to) add("assigned_to", "Assignee", `#${f.assigned_to}`);
  if (f.date_from) add("date_from", "From", f.date_from);
  if (f.date_to) add("date_to", "To", f.date_to);
  if (f.price_min) add("price_min", "Price ≥", f.price_min);
  if (f.price_max) add("price_max", "Price ≤", f.price_max);
  if (f.overdue) add("overdue", "Overdue", f.overdue);

  return chips.map((c) => (
    <Chip key={c.key} label={c.label} size="small" onDelete={() => onRemove(c.key)} />
  ));
};

const StatCard = ({ label, value, icon, color, loading }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ bgcolor: `${color}.main`, width: 40, height: 40 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={28} />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {value ?? 0}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default function Jobs() {
  const navigate = useNavigate();
  const { can } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickSearch, setQuickSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);

  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  const loadJobs = useCallback(async (pageNo, pageSize, f) => {
    setLoading(true);
    try {
      const res = await api.get(`/jobs/list.php?${toQuery(pageNo, pageSize, f)}`);
      setJobs(res.jobs || []);
      setPagination(res.pagination || { page: pageNo, pageSize, totalPages: 0, totalRecords: 0 });
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load jobs" });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get("/jobs/stats.php");
      setStats(res.stats || {});
    } catch (e) {
      // non-fatal
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs(1, 10, emptyFilters);
    loadStats();
  }, [loadJobs, loadStats]);

  const handleQuickSearch = () => {
    const next = { ...filters, search: quickSearch };
    setFilters(next);
    loadJobs(1, pagination.pageSize, next);
  };

  const handleApplyFilters = (next) => {
    setFilters(next);
    setQuickSearch(next.search || "");
    loadJobs(1, pagination.pageSize, next);
  };

  const handleRemoveChip = (key) => {
    const next = { ...filters };
    if (Array.isArray(next[key])) next[key] = [];
    else next[key] = "";
    if (key === "search") setQuickSearch("");
    setFilters(next);
    loadJobs(1, pagination.pageSize, next);
  };

  const handleClearAll = () => {
    setFilters(emptyFilters);
    setQuickSearch("");
    loadJobs(1, pagination.pageSize, emptyFilters);
  };

  const handleAutoGenerate = async () => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "Run Auto-Generate?",
      text: "This will scan all active subscriptions and create due jobs for the current cycle. Safe to re-run — duplicates are skipped.",
      showCancelButton: true,
      confirmButtonText: "Run Now",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setAutoGenLoading(true);
    try {
      const res = await api.post("/jobs/auto_generate.php", {});
      const s = res.summary || {};
      await Swal.fire({
        icon: "success",
        title: "Auto-Generation Complete",
        html: `
          <div style="text-align:left;font-size:14px">
            <div>Scanned: <b>${s.scanned ?? 0}</b></div>
            <div>Created: <b style="color:green">${s.created ?? 0}</b></div>
            <div>Skipped: <b>${s.skipped ?? 0}</b></div>
            <div>Errors: <b style="color:red">${s.errors ?? 0}</b></div>
          </div>
        `,
      });
      loadJobs(1, pagination.pageSize, filters);
      loadStats();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Auto-Gen Failed", text: err.message });
    } finally {
      setAutoGenLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${API_BASE}/jobs/export.php?${toQuery(1, 1, filters)}`;
      const res = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `jobs_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Export Failed", text: err.message });
    }
  };

  const activeCount = countActive(filters);
  const chips = activeChips(filters, handleRemoveChip);

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <WorkIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Jobs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All client jobs and their progress
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          {can("jobs.auto_generate") && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<AutorenewIcon />}
              onClick={handleAutoGenerate}
              disabled={autoGenLoading}
            >
              {autoGenLoading ? "Running..." : "Run Auto-Gen"}
            </Button>
          )}
          {can("jobs.export") && (
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>
              Export
            </Button>
          )}
          {can("jobs.create") && (
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => navigate("/job/create")}
            >
              Create Job
            </Button>
          )}
        </Stack>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {/* STATS STRIP */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Jobs"
            value={stats.total}
            icon={<WorkIcon />}
            color="primary"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="In Progress"
            value={stats.in_progress}
            icon={<HourglassTopIcon />}
            color="info"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Completed"
            value={stats.completed}
            icon={<CheckCircleIcon />}
            color="success"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Overdue"
            value={stats.overdue}
            icon={<WarningAmberIcon />}
            color="error"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Value"
            value={stats.total_value != null ? `₹${Number(stats.total_value).toLocaleString("en-IN")}` : "₹0"}
            icon={<PaymentsIcon />}
            color="secondary"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="This Month"
            value={stats.this_month}
            icon={<AddCircleIcon />}
            color="warning"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Manual"
            value={stats.manual_count}
            icon={<WorkIcon />}
            color="primary"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Auto (Cron)"
            value={stats.cron_count}
            icon={<HourglassTopIcon />}
            color="info"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* SEARCH + FILTER BAR */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Quick search by client, job id, remark"
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleQuickSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <Button variant="contained" onClick={handleQuickSearch} startIcon={<SearchIcon />}>
            Search
          </Button>
          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterAltIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>
        </Stack>

        {chips.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}
            alignItems="center"
          >
            {chips}
            <Button size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          </Stack>
        )}
      </Paper>

      {/* TABLE */}
      <Paper>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Services</TableCell>
                <TableCell>Total Price</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Overdue</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                [...Array(5)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={9}>
                      <Skeleton height={36} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography variant="h3" sx={{ mb: 1 }}>📁</Typography>
                    <Typography variant="body2" color="text.secondary">
                      No jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                jobs.map((j) => {
                  const total = Number(j.total_services) || 0;
                  const done = Number(j.completed_services) || 0;
                  const pct = total > 0 ? (done / total) * 100 : 0;
                  const overdueCount = Number(j.overdue_count) || 0;

                  return (
                    <TableRow key={j.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          JOB#{j.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {j.created_at}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {j.client_name || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {j.manager_name || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {done}/{total}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{ mt: 0.5, height: 6, borderRadius: 3, width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          ₹{Number(j.total_price || 0).toLocaleString("en-IN")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={j.source === "cron" ? "Auto" : "Manual"}
                          color={j.source === "cron" ? "info" : "secondary"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={j.status || "Pending"}
                          color={statusColor(j.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {overdueCount > 0 ? (
                          <Chip
                            size="small"
                            label={overdueCount}
                            color="error"
                            icon={<WarningAmberIcon />}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/jobs/${j.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Box>

        <Divider />

        <TablePagination
          component="div"
          count={pagination.totalRecords || 0}
          page={Math.max(0, (pagination.page || 1) - 1)}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => loadJobs(p + 1, pagination.pageSize, filters)}
          onRowsPerPageChange={(e) => loadJobs(1, parseInt(e.target.value, 10), filters)}
        />
      </Paper>

      {/* FILTER DRAWER */}
      <JobFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />
    </Box>
  );
}
